import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { TrendingUp, MousePointerClick, DollarSign } from "lucide-react";

export default function CampaignsPanel() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get(`/admin/campaigns?days=${days}`, { withCredentials: true })
      .then((r) => setData(r.data));
  }, [days]);

  if (!data) return <div className="text-white/50 text-sm">Loading campaign data…</div>;

  return (
    <section data-testid="campaigns-panel">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <h2 className="font-display text-2xl text-white">Ad Campaigns</h2>
        <div className="flex gap-2">
          {[7, 30, 90].map((d) => (
            <button
              key={d}
              onClick={() => setDays(d)}
              data-testid={`campaigns-range-${d}`}
              className={`text-xs uppercase tracking-widest px-3 py-1.5 rounded-full transition-colors ${
                days === d
                  ? "bg-[#FF3D8B] text-black font-bold"
                  : "border border-white/15 text-white/70 hover:border-white/40"
              }`}
            >
              {d}d
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="rounded-xl border border-white/10 bg-[#0f0f0f] p-5">
          <div className="flex items-center gap-2 text-white/50 text-xs uppercase tracking-widest mb-2">
            <MousePointerClick size={14} className="text-[#FF3D8B]" /> Visits
          </div>
          <div className="font-display text-3xl text-white">{data.totals.visits}</div>
        </div>
        <div className="rounded-xl border border-white/10 bg-[#0f0f0f] p-5">
          <div className="flex items-center gap-2 text-white/50 text-xs uppercase tracking-widest mb-2">
            <TrendingUp size={14} className="text-[#A855F7]" /> Orders
          </div>
          <div className="font-display text-3xl text-white">{data.totals.orders_paid}</div>
        </div>
        <div className="rounded-xl border border-white/10 bg-[#0f0f0f] p-5">
          <div className="flex items-center gap-2 text-white/50 text-xs uppercase tracking-widest mb-2">
            <DollarSign size={14} className="text-[#FF3D8B]" /> Revenue
          </div>
          <div className="font-display text-3xl text-white">${data.totals.revenue.toFixed(2)}</div>
        </div>
      </div>

      <div className="rounded-xl border border-white/10 bg-[#0f0f0f] overflow-hidden">
        <table className="w-full text-sm">
          <thead className="text-white/50 text-[11px] uppercase tracking-widest">
            <tr>
              <th className="text-left p-4">Source</th>
              <th className="text-left p-4">Campaign</th>
              <th className="text-left p-4">Visits</th>
              <th className="text-left p-4">Cart adds</th>
              <th className="text-left p-4">Orders</th>
              <th className="text-left p-4">Revenue</th>
              <th className="text-left p-4">CR</th>
              <th className="text-left p-4">AOV</th>
            </tr>
          </thead>
          <tbody>
            {data.rows.length === 0 ? (
              <tr>
                <td colSpan={8} className="p-8 text-center text-white/40 text-sm">
                  No campaign data yet. Point Google/Meta ads at a city URL with{" "}
                  <code className="text-[#FF3D8B]">?utm_source=google&amp;utm_campaign=miami-sexy</code>
                  {" "}to start attributing.
                </td>
              </tr>
            ) : (
              data.rows.map((r) => (
                <tr key={`${r.source}::${r.campaign}`} className="border-t border-white/5" data-testid={`campaign-row-${r.source}-${r.campaign}`}>
                  <td className="p-4 text-white/80">{r.source}</td>
                  <td className="p-4 text-white/80">{r.campaign}</td>
                  <td className="p-4 text-white">{r.visits}</td>
                  <td className="p-4 text-white/70">{r.cart_adds}</td>
                  <td className="p-4 text-white font-semibold">{r.orders_paid}</td>
                  <td className="p-4 text-[#FF3D8B] font-semibold">${r.revenue.toFixed(2)}</td>
                  <td className="p-4 text-white/70">{r.conversion_rate}%</td>
                  <td className="p-4 text-white/70">${r.aov}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="mt-6 text-white/40 text-xs">
        Attribution captured via <code>utm_source</code>, <code>utm_medium</code>, <code>utm_campaign</code>,{" "}
        <code>utm_content</code>, <code>utm_term</code>, <code>gclid</code>, <code>fbclid</code> on any landing page.
        Add <code>?promo=WELCOME10</code> (or any coupon code) to auto-apply at checkout.
      </div>
    </section>
  );
}
